'use client';

import { startTransition, useState, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { type ProductPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPriceFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import type { MessageFragmentFragmentDoc } from '@packages/gql/generated/shopping/MessageFragmentFragmentDoc';
import type { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import type { TrackGlycerinAddToCartLineItemFragmentDoc } from '@packages/gql/generated/shopping/TrackGlycerinAddToCartLineItemFragmentDoc';

import { selectedQuantity, selectedServices } from '../atoms';
import type { UseAddToBasketTrackingProps } from '../Tracking/hooks/useAddToBasketTracking/useAddToBasketTracking';
import { useAddToBasketTracking } from '../Tracking/hooks/useAddToBasketTracking/useAddToBasketTracking';
import { addToBasketMessages } from './messages';
import { getProductFromLineItemsBySku } from './helper/getProductFromLineItemsBySku/getProductFromLineItemsBySku';
import { getAplusDataForBasket } from '../APlus/helper/getAplusDataForBasket';
import { useChangeProductInBasket } from './hooks/useChangeProductInBasket/useChangeProductInBasket';
import { useAddProductToBasket } from './hooks/useAddProductToBasket/useAddProductToBasket';

export type UseBasketDataProps = UseAddToBasketTrackingProps & {
  lineItemId?: string;
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  customButtonText?: MessageDescriptor | null;
};

export const useBasketData = ({
  trackingProductData,
  lineItemId,
  onClose,
  addToBasketTrackingAplus,
}: UseBasketDataProps) => {
  const {
    akl,
    sku,
    price: priceData,
    availability: { state, isOrderable },
  } = trackingProductData;

  const price = unmask<typeof ProductPriceFragmentFragmentDoc>(priceData);

  const [dialogOpened, setDialogOpened] = useState(false);

  const quantity = useAtomValue(selectedQuantity);
  const aPlusData = useAtomValue(selectedServices);

  const { formatMessage } = useIntl();
  const { jwtToken } = useSession();
  const { setDefaultProductOutcome } = useAbTesting();
  const { locale } = useI18n();
  const { addNotification } = useNotification();

  const isAvailable = state !== 'UNSELECTED' ? isOrderable : true;

  const {
    data: addToBasketMutationResult,
    fetching: fetchingAddProductToBasket,
    addProductToBasket,
  } = useAddProductToBasket();
  const { fetching: changeProductFetching, changeProductInBasket } = useChangeProductInBasket();
  const {
    trackLayerClosed,
    trackToBasketClick,
    trackToCheckAndOrder,
    trackAddToCart,
    trackCatalogAddedProduct,
    trackGlycerinAddToCart,
    getTrackingParameter,
    trackUpdateCart,
  } = useAddToBasketTracking({
    trackingProductData,
    addToBasketTrackingAplus,
  });

  const isLoading = !jwtToken || fetchingAddProductToBasket || changeProductFetching;

  // It works only for miniDV to update product.
  const miniDVOnClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const result = await changeProductInBasket({
      locale,
      sku,
      lineItemId: lineItemId!,
      count: quantity,
      serviceLineitems: getAplusDataForBasket(aPlusData),
    });

    if (result.success === false) {
      addNotification({
        message: formatMessage(addToBasketMessages.errorMessage),
        severity: 'error',
        duration: 5000,
      });
    } else {
      addNotification({
        message: formatMessage(addToBasketMessages.miniDVSuccess),
        severity: 'success',
        duration: 5000,
      });
      trackUpdateCart(result.data.lineItems, lineItemId);
      if (onClose && typeof onClose === 'function') {
        onClose(event);
      }
    }
  };

  // It works for both miniDV and detail view page to add product.
  const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (state === 'UNSELECTED') {
      addNotification({
        message: formatMessage(addToBasketMessages.warningMessage),
        severity: 'warning',
        duration: 5000,
      });
    } else {
      const result = await addProductToBasket({
        locale,
        sku,
        count: quantity,
        isSpecialOrder: false,
        serviceLineitems: getAplusDataForBasket(aPlusData),
        trackingParameter: getTrackingParameter(),
      });
      // todo: shouldn't this only be fired in case of onClose being called?
      if (onClose && typeof onClose === 'function') {
        onClose(event);
      }

      if (result.success === false) {
        const message =
          result.messages && result.messages.length > 0
            ? unmask<typeof MessageFragmentFragmentDoc>(result.messages)[0].description
            : formatMessage(addToBasketMessages.errorMessage);

        addNotification({
          message,
          severity: 'error',
          duration: 5000,
        });
        setDialogOpened(false);
      } else {
        startTransition(() => setDialogOpened(true));

        const addedLineItem = getProductFromLineItemsBySku(result.data.lineItems, sku);
        const trackingLineItem = addedLineItem
          ? unmask<typeof TrackGlycerinAddToCartLineItemFragmentDoc>(addedLineItem)
          : undefined;
        const lineItemProduct = trackingLineItem?.product ?? null;
        const lineItemExtra = trackingLineItem?.extra ?? null;

        setDefaultProductOutcome(
          [
            {
              count: quantity,
              masterId: akl,
              sku: lineItemProduct
                ? `${lineItemProduct.orderNumber}${lineItemExtra?.promotion ?? ''}-${lineItemProduct.size ?? ''}`
                : sku,
              value: price.value,
            },
          ],
          'ATB',
        );

        trackToCheckAndOrder();
        trackAddToCart({ lineItems: result.data.lineItems });
        trackGlycerinAddToCart({ lineItems: result.data.lineItems });
        trackCatalogAddedProduct({ lineItems: result.data.lineItems });
      }
    }
  };

  const trackToCheckAndOrderHandler = () => {
    trackToBasketClick();
  };

  const layerCloseHandler = () => {
    startTransition(() => setDialogOpened(false));
    trackLayerClosed();
  };

  const lineItemForDialog: LineItemFragmentFragment | undefined = useMemo(() => {
    if (
      addToBasketMutationResult?.addToBasket.success &&
      addToBasketMutationResult.addToBasket.lineItems.length > 0
    ) {
      const lineItem = getProductFromLineItemsBySku(
        addToBasketMutationResult.addToBasket.lineItems,
        sku,
      );

      return lineItem ? unmask<typeof LineItemFragmentFragmentDoc>(lineItem) : undefined;
    }
    return undefined;
  }, [addToBasketMutationResult, sku]);

  return {
    isAvailable,
    isLoading,
    dialogOpened,
    miniDVOnClickHandler,
    onClickHandler,
    trackToCheckAndOrderHandler,
    layerCloseHandler,
    lineItemForDialog,
  };
};
